import React, { useEffect, useState } from 'react';

import { useRouter } from 'next/router';

import MicroTour from './MicroTour';
import { TourStep } from './tours';

const SEEN_KEY = 'onark-modal-tour-seen';

function readSeen(): Record<string, boolean> {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(sessionStorage.getItem(SEEN_KEY) || '{}') as Record<
      string,
      boolean
    >;
  } catch {
    return {};
  }
}

function markSeen(name: string) {
  if (typeof window === 'undefined') return;
  try {
    const seen = readSeen();

    seen[name] = true;
    sessionStorage.setItem(SEEN_KEY, JSON.stringify(seen));
  } catch {
    // ignore
  }
}

/**
 * Auto-fires a one-time spotlight tour the first time something opens —
 * meant for modals and modal-shaped UI (class settings, etc.). Distinct
 * from:
 *
 *   - The onboarding walkthrough's per-step Callout/MicroTour, which
 *     fires on `?onboarding=<step>`.
 *   - SectionTypeTour, which fires per section editor type.
 *
 * Mount this inside whatever opens, pass it a stable `name` (used as the
 * session-seen key) and the steps. The hosting component should already
 * be conditionally rendered (e.g. inside a modal that only renders when
 * open), so mounting === "the user just opened this".
 *
 * Suppressed while the main onboarding walkthrough is engaged so the two
 * popovers don't fight over screen real estate.
 */
interface ModalTourProps {
  name: string;
  steps: TourStep[];
}

const ModalTour: React.FC<ModalTourProps> = ({ name, steps }) => {
  const router = useRouter();
  const [active, setActive] = useState(false);
  const onboardingActive = !!router.query.onboarding;

  useEffect(() => {
    if (onboardingActive) return;
    if (readSeen()[name]) return;
    // Small delay so the modal's contents (and our anchors) are laid out
    // before the spotlight tries to measure them.
    const t = window.setTimeout(() => {
      setActive(true);
      markSeen(name);
    }, 400);

    return () => window.clearTimeout(t);
  }, [name, onboardingActive]);

  if (!active) return null;

  return <MicroTour steps={steps} onClose={() => setActive(false)} />;
};

export default ModalTour;
